import { DATE_PIPE_DEFAULT_OPTIONS, DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import localeNl from '@angular/common/locales/nl';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { FaConfig } from '@fortawesome/angular-fontawesome';
import { NgxHateoasClientConfigurationService } from '@lagoshny/ngx-hateoas-client';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
  AutoRefreshTokenService,
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
  UserActivityService,
} from 'keycloak-angular';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import { environment } from '../environments/environment';
import { CapitalizePipe } from './shared/pipes/capitalize.pipe';
import { UserInfoPipe } from './shared/pipes/user-info.pipe';
import { routes } from './app.routes';
import { Company } from './models/company/company-models.model';
import { FileDocument } from './models/company/document.model';
import { ElementCode } from './models/element/element-code.model';
import { ElementGroupCode } from './models/element/element-group-code.model';
import { InspectionListItemCode } from './models/inspection/inspection-list-item-code.model';
import { InspectionMethodCode } from './models/inspection/inspection-method-code.model';
import { DocumentCode } from './models/labels/document-code.model';
import { LayerFeaturesCode } from './models/labels/layer-features-code.model';
import { ObjectCategoryCode } from './models/labels/object-category-code.model';
import { RgsObject } from './models/objects/rgs-object.model';
import { PricebookCode } from './models/pricebook/pricebook-code.model';
import { Scenario } from './models/scenario/scenario.model';
import { StructureCode } from './models/structure/structure-code.model';
import { UserProfile } from './models/userprofile/user-profile.model';
import { InspectionItemCode } from './rgs/components/objects/objectItem/objectInspection/models/inspection-item-code.model';
import { Inspection } from './rgs/components/objects/objectItem/objectInspection/models/inspection.model';
import { Note } from './rgs/components/objects/objectItem/objectInspection/models/note.model';
import { rgsPreset } from './rgsPreset';
import { I18nVersionService } from './services/i18n-version/i18n-version.service';
import { VersionedTranslateLoader } from './services/i18n-version/versioned-translate.loader';
import { LicenseInterceptor } from './shared/interceptors/license.interceptor';
import { TenantInterceptor } from './shared/interceptors/tenant.interceptor';
import { sessionInterceptor } from './shared/interceptors/session.interceptor';
import { ErrorHandlerService } from './shared/services/error-handler.service';
import { DATE_FORMAT } from './shared/utils/local-date.utils';

registerLocaleData(localeNl, 'nl');

export function httpTranslateLoader(http: HttpClient, i18nVersionService: I18nVersionService) {
  return new VersionedTranslateLoader(http, i18nVersionService);
}

export function initializeI18nVersion() {
  return inject(I18nVersionService).load();
}

export function initializeHateoas() {
  const hateoasConfig = inject(NgxHateoasClientConfigurationService);
  hateoasConfig.configure({
    http: {
      rootUrl: environment.springAppUrl,
    },
    useTypes: {
      resources: [
        RgsObject,
        StructureCode,
        UserProfile,
        PricebookCode,
        ElementGroupCode,
        ElementCode,
        DocumentCode,
        LayerFeaturesCode,
        ObjectCategoryCode,
        Inspection,
        InspectionListItemCode,
        InspectionMethodCode,
        InspectionItemCode,
        Note,
        Scenario,
        FileDocument,
        Company,
      ],
    },
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withInMemoryScrolling({ anchorScrolling: 'enabled' })),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpTranslateLoader,
          deps: [HttpClient, I18nVersionService],
        },
      }),
    ),
    DatePipe,
    provideAnimationsAsync(),
    providePrimeNG({
      ripple: true,
      theme: {
        preset: rgsPreset,
        options: {
          darkModeSelector: false || 'none',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
    }),
    provideKeycloak({
      config: environment.keycloak_config,
      initOptions: {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
      },
      providers: [
        AutoRefreshTokenService,
        UserActivityService,
        {
          provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
          useValue: [
            createInterceptorCondition<IncludeBearerTokenCondition>({
              urlPattern: /^(?!.*maps\.googleapis\.com).*/,
            }),
          ],
        },
      ],
    }),
    provideHttpClient(withInterceptors([includeBearerTokenInterceptor, sessionInterceptor])),
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: LOCALE_ID, useValue: 'nl-NL' },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: DATE_FORMAT } },
    MessageService,
    ConfirmationService,
    DialogService,
    CapitalizePipe,
    UserInfoPipe,
    provideAppInitializer(initializeI18nVersion),
    provideAppInitializer(initializeHateoas),
    { provide: HTTP_INTERCEPTORS, useClass: LicenseInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TenantInterceptor, multi: true },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    {
      provide: FaConfig,
      useValue: {
        fixedWidth: true,
      },
    },
  ],
};
